import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const BlogPreview = () => {
  const blogPosts = [
    {
      id: 1,
      title: "Building Scalable React Applications: A Complete Guide",
      excerpt: "Learn the essential patterns and best practices for creating React applications that can grow with your business needs. From component architecture to state management strategies.",
      category: "Technical Tutorial",
      readTime: "12 min read",
      publishDate: "2024-09-05",
      author: "Adrian",
      image: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=250&fit=crop",
      tags: ["React", "Architecture", "Best Practices"],
      engagement: {
        views: "200",
        likes: "156",
        comments: "23",
        shares: "45"
      },
      featured: true
    },
    {
      id: 2,
      title: "From Bootcamp to Big Tech: A Mentee\'s Success Story",
      excerpt: "Follow Sarah's incredible journey from coding bootcamp graduate to landing her dream job at Google. Discover the strategies, mindset shifts, and support systems that made it possible.",
      category: "Mentorship Insights",
      readTime: "8 min read",
      publishDate: "2024-09-03",
      author: "Adrian",
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=250&fit=crop",
      tags: ["Career Growth", "Mentorship", "Success Story"],
      engagement: {
        views: "3.1K",
        likes: "234",
        comments: "67",
        shares: "89"
      },
      featured: false
    },
    {
      id: 3,
      title: "Building Developer Communities That Actually Matter",
      excerpt: "The secrets behind creating thriving developer communities. Learn how CodersHub grew to 15,000+ members across 45 countries and the principles that drive real engagement.",
      category: "Community Building",
      readTime: "10 min read",
      publishDate: "2024-09-01",
      author: "Adrian",
      image: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=400&h=250&fit=crop",
      tags: ["Community", "Leadership", "Growth"],
      engagement: {
        views: "1.8K",
        likes: "98",
        comments: "34",
        shares: "56"
      },
      featured: false
    }
  ];

  const categories = [
    { name: "Technical Tutorial", count: 24, icon: "Code", color: "text-blue-600" },
    { name: "Mentorship Insights", count: 18, icon: "Users", color: "text-emerald-600" },
    { name: "Community Building", count: 12, icon: "Globe", color: "text-purple-600" },
    { name: "Career Growth", count: 15, icon: "TrendingUp", color: "text-orange-600" }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="BookOpen" size={20} className="text-primary" />
            <span className="text-sm font-brand-medium text-primary tracking-wide uppercase">Knowledge Hub</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-brand-headline text-foreground mb-6">
            Latest Insights &
            <span className="block text-primary text-gradient-gold">Technical Tutorials</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto font-brand-body">
            Sharing knowledge through in-depth technical guides, mentorship insights, 
            and community building strategies that help developers grow.
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Featured Post */}
            <div className="mb-12">
              <div className="bg-card rounded-2xl overflow-hidden border border-border brand-shadow-lg">
                <div className="relative h-64 sm:h-80">
                  <Image
                    src={blogPosts?.[0]?.image}
                    alt={blogPosts?.[0]?.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-transparent to-transparent"></div>
                  
                  {/* Featured Badge */}
                  <div className="absolute top-4 left-4">
                    <span className="inline-flex items-center px-3 py-1 bg-primary text-primary-foreground text-sm font-brand-medium rounded-full">
                      <Icon name="Star" size={14} className="mr-1" />
                      Featured
                    </span>
                  </div>

                  {/* Category Badge */}
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-background/90 text-foreground text-sm font-brand-medium rounded-full border border-border">
                      {blogPosts?.[0]?.category}
                    </span>
                  </div>

                  {/* Content Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-2xl font-brand-subheading mb-2">
                      {blogPosts?.[0]?.title}
                    </h3>
                    <p className="text-gray-200 font-brand-body mb-4">
                      {blogPosts?.[0]?.excerpt}
                    </p>
                    
                    {/* Meta Info */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm text-gray-300">
                        <span className="flex items-center">
                          <Icon name="Calendar" size={14} className="mr-1" />
                          {formatDate(blogPosts?.[0]?.publishDate)}
                        </span>
                        <span className="flex items-center">
                          <Icon name="Clock" size={14} className="mr-1" />
                          {blogPosts?.[0]?.readTime}
                        </span>
                      </div>
                      
                      {/* Engagement Stats */}
                      <div className="flex items-center space-x-3 text-sm text-gray-300">
                        <span className="flex items-center">
                          <Icon name="Eye" size={14} className="mr-1" />
                          {blogPosts?.[0]?.engagement?.views}
                        </span>
                        <span className="flex items-center">
                          <Icon name="Heart" size={14} className="mr-1" />
                          {blogPosts?.[0]?.engagement?.likes}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Posts Grid */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {blogPosts?.slice(1)?.map((post) => (
                <article
                  key={post?.id}
                  className="bg-card rounded-xl overflow-hidden border border-border hover:border-primary/30 transition-all duration-300 brand-shadow hover:brand-shadow-lg group"
                >
                  {/* Post Image */}
                  <div className="relative h-48 overflow-hidden">
                    <Image
                      src={post?.image}
                      alt={post?.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    
                    {/* Category Badge */}
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-1 bg-background/90 text-foreground text-xs font-brand-medium rounded-full border border-border">
                        {post?.category}
                      </span>
                    </div>
                  </div>

                  {/* Post Content */}
                  <div className="p-6">
                    <h3 className="text-lg font-brand-subheading text-foreground mb-2 group-hover:text-primary transition-colors">
                      {post?.title}
                    </h3>
                    <p className="text-text-secondary font-brand-body text-sm leading-relaxed mb-4">
                      {post?.excerpt}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post?.tags?.slice(0, 2)?.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-muted text-text-secondary text-xs font-brand-body rounded border border-border"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Meta Info */}
                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <div className="flex items-center space-x-3 text-xs text-text-secondary">
                        <span className="flex items-center">
                          <Icon name="Calendar" size={12} className="mr-1" />
                          {formatDate(post?.publishDate)}
                        </span>
                        <span className="flex items-center">
                          <Icon name="Clock" size={12} className="mr-1" />
                          {post?.readTime}
                        </span>
                      </div>
                      
                      {/* Engagement */}
                      <div className="flex items-center space-x-2 text-xs text-text-secondary">
                        <span className="flex items-center">
                          <Icon name="Eye" size={12} className="mr-1" />
                          {post?.engagement?.views}
                        </span>
                        <span className="flex items-center">
                          <Icon name="MessageCircle" size={12} className="mr-1" />
                          {post?.engagement?.comments}
                        </span>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* View All Posts CTA */}
            <div className="text-center">
              <Link to="/blogpage">
                <Button
                  variant="outline"
                  size="lg"
                  iconName="ArrowRight"
                  iconPosition="right"
                  iconSize={20}
                  className="border-primary/30 text-primary hover:bg-primary/10"
                >
                  View All Posts
                </Button>
              </Link>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="space-y-8">
              {/* Categories */}
              <div className="bg-card rounded-xl p-6 border border-border brand-shadow">
                <h3 className="text-lg font-brand-subheading text-foreground mb-4 flex items-center">
                  <Icon name="FolderOpen" size={18} className="mr-2 text-primary" />
                  Categories
                </h3>
                <div className="space-y-3">
                  {categories?.map((category, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-primary/5 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center space-x-3">
                        <Icon name={category?.icon} size={16} className={category?.color} />
                        <span className="text-sm font-brand-medium text-foreground">
                          {category?.name}
                        </span>
                      </div>
                      <span className="text-xs text-text-secondary bg-background px-2 py-1 rounded-full">
                        {category?.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Newsletter Signup */}
              <div className="bg-gradient-brand rounded-xl p-6 text-white">
                <div className="text-center">
                  <Icon name="Mail" size={32} className="text-primary mx-auto mb-4" />
                  <h3 className="text-lg font-brand-subheading mb-2">
                    Stay Updated
                  </h3>
                  <p className="text-gray-300 font-brand-body text-sm mb-4">
                    Get the latest tutorials, insights, and community updates delivered to your inbox.
                  </p>
                  <Button
                    variant="default"
                    fullWidth
                    iconName="ArrowRight"
                    iconPosition="right"
                    iconSize={16}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    Subscribe Now
                  </Button>
                </div>
              </div>

              {/* Popular Tags */}
              <div className="bg-card rounded-xl p-6 border border-border brand-shadow">
                <h3 className="text-lg font-brand-subheading text-foreground mb-4 flex items-center">
                  <Icon name="Tag" size={18} className="mr-2 text-primary" />
                  Popular Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {["React", "JavaScript", "Career", "Mentorship", "Community", "Node.js", "TypeScript", "Leadership"]?.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-muted text-text-secondary text-sm font-brand-body rounded-full hover:bg-primary/10 hover:text-primary transition-colors cursor-pointer"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogPreview;